import { useState, useEffect, useMemo } from "react";
import { Status, Priority, SalaryPeriod, STATUS_ORDER, STATUS_LABELS, NETWORKING_STATUS_ORDER, NETWORKING_STATUS_LABELS, DISPOSITION_ORDER, DISPOSITION_LABELS } from "@launchpad/shared";
import { createApplication, updateApplication, updateStatus } from "../../api/client.js";
import { useAppStore } from "../../store/useAppStore.js";
import { useToastStore } from "../../store/useToastStore.js";
import { ChecklistPanel } from "./ChecklistPanel.jsx";
import { DebriefSection } from "./DebriefSection.jsx";
import { ActivityTimeline } from "./ActivityTimeline.jsx";
import { DuplicateWarning, findDuplicate } from "./DuplicateWarning.jsx";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog.jsx";
import { Modal } from "../common/Modal.jsx";

export function ApplicationModal({ app, boardType: boardTypeProp, onClose }) {
   const applications = useAppStore((s) => s.applications);
   const addApplicationLocal = useAppStore((s) => s.addApplicationLocal);
   const updateApplicationLocal = useAppStore((s) => s.updateApplicationLocal);
   const addToast = useToastStore((s) => s.addToast);
   const isNetworking = app?.boardType === 'networking' || (!app && boardTypeProp === 'networking');

   const isEdit = !!app?.appId;

   const [tab, setTab] = useState("form"); // form | timeline | notes | jobDesc
   const [form, setForm] = useState(getInitialForm(app, isNetworking));
   const [checklist, setChecklist] = useState(app?.checklist || []);
   const [debrief, setDebrief] = useState(app?.debrief || []);
   const [tagInput, setTagInput] = useState("");
   const [submitting, setSubmitting] = useState(false);
   const [error, setError] = useState("");
   const [duplicate, setDuplicate] = useState(null);
   const [showDelete, setShowDelete] = useState(false);

   const tagSuggestions = useMemo(() => {
      if (!tagInput.trim()) return [];
      const allTags = [...new Set(applications.flatMap((a) => a.tags || []))];
      const query = tagInput.toLowerCase();
      return allTags.filter((t) => t.toLowerCase().includes(query) && !form.tags.includes(t)).slice(0, 5);
   }, [tagInput, applications, form.tags]);

   useEffect(() => {
      if (app) {
         setForm(getInitialForm(app, isNetworking));
         setChecklist(app.checklist || []);
         setDebrief(app.debrief || []);
      }
   }, [app]);

   function update(field, value) {
      setForm((f) => ({ ...f, [field]: value }));
   }

   function updateSalary(field, value) {
      setForm((f) => ({ ...f, salary: { ...f.salary, [field]: value } }));
   }

   function addTag() {
      const tag = tagInput.trim();
      if (tag && !form.tags.includes(tag)) update("tags", [...form.tags, tag]);
      setTagInput("");
   }

   function removeTag(tag) {
      update(
         "tags",
         form.tags.filter((t) => t !== tag),
      );
   }

   async function handleSubmit(e) {
      e.preventDefault();
      setError("");

      if (!form.company.trim() || !form.role.trim()) {
         setError("Company and Role are required");
         return;
      }

      // Duplicate detection (create only)
      if (!isEdit && !duplicate) {
         const match = findDuplicate(applications, form.company, form.role);
         if (match) {
            setDuplicate(match);
            return;
         }
      }

      setSubmitting(true);
      setDuplicate(null);

      const payload = {
         ...form,
         checklist,
         debrief,
         disposition: isNetworking ? undefined : (form.disposition || null),
         dispositionNote: isNetworking ? undefined : (form.disposition ? (form.dispositionNote || null) : null),
         salary:
            form.salary.min || form.salary.max
               ? {
                    ...form.salary,
                    min: Number(form.salary.min) || 0,
                    max: Number(form.salary.max) || 0,
                 }
               : undefined,
      };

      try {
         if (isEdit) {
            const updated = await updateApplication(app.appId, payload);
            updateApplicationLocal(app.appId, updated);
            // Handle status change separately (PUT ignores status field)
            if (form.status !== app.status) {
               await updateStatus(app.appId, form.status);
               updateApplicationLocal(app.appId, { status: form.status });
            }
         } else {
            const created = await createApplication({ ...payload, boardType: isNetworking ? 'networking' : 'application' });
            addApplicationLocal(created);
         }
         onClose();
         addToast(isEdit ? 'Saved ✓' : 'Created ✓', 'success');
      } catch (err) {
         setError(err.message || "Failed to save");
      } finally {
         setSubmitting(false);
      }
   }

   async function handleConvert() {
      setSubmitting(true);
      setError("");
      try {
         const updated = await updateApplication(app.appId, { boardType: 'application' });
         await updateStatus(app.appId, Status.APPLIED);
         updateApplicationLocal(app.appId, { ...updated, boardType: 'application', status: Status.APPLIED });
         addToast('Converted to application ✓', 'success');
         onClose();
      } catch (err) {
         setError(err.message || "Failed to convert");
      } finally {
         setSubmitting(false);
      }
   }

   async function handleDuplicate() {
      setSubmitting(true);
      setError("");
      try {
         const payload = {
            company: app.company,
            role: app.role || 'TBD',
            status: app.status,
            boardType: app.boardType,
            tags: app.tags || [],
            notes: app.notes || '',
            source: app.source || '',
            priority: app.priority || '',
         };
         const created = await createApplication(payload);
         addApplicationLocal(created);
         addToast('Card duplicated ✓', 'success');
         onClose();
      } catch (err) {
         setError(err.message || "Failed to duplicate");
      } finally {
         setSubmitting(false);
      }
   }

   // Debrief visibility: interviewing OR transitioned from interviewing to rejected
   const showDebrief =
      isEdit &&
      (app.status === Status.INTERVIEWING ||
         app.statusHistory?.some(
            (e) => e.from === Status.INTERVIEWING && e.to === Status.REJECTED,
         ));

   return (
      <Modal onClose={onClose} className="max-w-2xl">
         <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
            <h2 className="text-base font-semibold">
               {isEdit ? (isNetworking ? "Edit Company" : "Edit Application") : (isNetworking ? "New Company" : "New Application")}
            </h2>
            <button
               onClick={onClose}
               className="text-slate-400 hover:text-slate-600 text-xl leading-none"
            >
               &times;
            </button>
         </div>

         {/* Tabs (edit mode only) */}
         {isEdit && (
            <div className="flex border-b border-slate-200 px-5 text-xs">
               {[
                  ["form", "Details"],
                  ["timeline", "Timeline"],
                  ["notes", "Notes"],
                  ["jobDesc", "Job Description"],
               ].map(([key, label]) => (
                  <button
                     key={key}
                     onClick={() => setTab(key)}
                     className={`px-3 py-2 border-b-2 ${tab === key ? "border-primary-600 text-primary-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                  >
                     {label}
                  </button>
               ))}
            </div>
         )}

         <div className="max-h-[65vh] overflow-y-auto p-5">
            {duplicate && (
               <DuplicateWarning
                  match={duplicate}
                  onProceed={() => {
                     setDuplicate(null);
                     handleSubmit(new Event("submit"));
                  }}
                  onCancel={() => setDuplicate(null)}
               />
            )}

            {tab === "form" && (
               <form
                  id="app-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
               >
                  {error && (
                     <p className="text-sm text-destructive-600 bg-destructive-500/10 rounded px-3 py-2">
                        {error}
                     </p>
                  )}

                  <div className={`grid ${isNetworking && !isEdit ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                     <Input
                        label="Company *"
                        value={form.company}
                        onChange={(v) => update("company", v)}
                     />
                     {(!isNetworking || isEdit) && (
                     <Input
                        label={isNetworking ? "Role" : "Role *"}
                        value={form.role}
                        onChange={(v) => update("role", v)}
                     />
                     )}
                  </div>

                  {isEdit && (
                     <Select
                        label="Status"
                        value={form.status}
                        onChange={(v) => update("status", v)}
                        options={isNetworking ? NETWORKING_STATUS_ORDER : STATUS_ORDER}
                        labels={isNetworking ? NETWORKING_STATUS_LABELS : STATUS_LABELS}
                     />
                  )}

                  {!isNetworking && (
                  <div className="grid grid-cols-2 gap-3">
                     <Input
                        label="Date Applied"
                        type="date"
                        value={form.dateApplied}
                        onChange={(v) => update("dateApplied", v)}
                     />
                     <Input
                        label="Location"
                        value={form.location}
                        onChange={(v) => update("location", v)}
                     />
                  </div>
                  )}

                  {!isNetworking && (
                  <Input
                     label="Job URL"
                     type="url"
                     value={form.jobUrl}
                     onChange={(v) => update("jobUrl", v)}
                  />
                  )}

                  {!isNetworking && (
                  <div className="grid grid-cols-4 gap-2">
                     <Input
                        label="Salary Min"
                        type="number"
                        value={form.salary.min}
                        onChange={(v) => updateSalary("min", v)}
                     />
                     <Input
                        label="Salary Max"
                        type="number"
                        value={form.salary.max}
                        onChange={(v) => updateSalary("max", v)}
                     />
                     <Select
                        label="Currency"
                        value={form.salary.currency}
                        onChange={(v) => updateSalary("currency", v)}
                        options={["USD", "EUR", "GBP", "CAD", "AUD"]}
                     />
                     <Select
                        label="Period"
                        value={form.salary.period}
                        onChange={(v) => updateSalary("period", v)}
                        options={Object.values(SalaryPeriod)}
                     />
                  </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                     <Select
                        label="Priority"
                        value={form.priority}
                        onChange={(v) => update("priority", v)}
                        options={["", ...Object.values(Priority)]}
                     />
                     <Input
                        label="Source"
                        value={form.source}
                        onChange={(v) => update("source", v)}
                     />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                     <Input
                        label="Contact Name"
                        value={form.contactName}
                        onChange={(v) => update("contactName", v)}
                     />
                     <Input
                        label="Contact Email"
                        type="email"
                        value={form.contactEmail}
                        onChange={(v) => update("contactEmail", v)}
                     />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                     <Input
                        label="Next Action"
                        value={form.nextAction}
                        onChange={(v) => update("nextAction", v)}
                     />
                     <Input
                        label="Next Action Date"
                        type="date"
                        value={form.nextActionDate}
                        onChange={(v) => update("nextActionDate", v)}
                     />
                  </div>

                  <Input
                     label="Last Contacted"
                     type="date"
                     value={form.lastContacted}
                     onChange={(v) => update("lastContacted", v)}
                  />

                  {/* Tags */}
                  <div>
                     <label className="block text-xs font-medium text-slate-600 mb-1">
                        Tags
                     </label>
                     <div className="flex gap-1 flex-wrap mb-1">
                        {form.tags.map((tag) => (
                           <span
                              key={tag}
                              className="flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs"
                           >
                              {tag}
                              <button
                                 type="button"
                                 onClick={() => removeTag(tag)}
                                 className="text-slate-400 hover:text-slate-600"
                              >
                                 &times;
                              </button>
                           </span>
                        ))}
                     </div>
                     <div className="relative">
                        <div className="flex gap-1">
                           <input
                              value={tagInput}
                              onChange={(e) => setTagInput(e.target.value)}
                              onKeyDown={(e) =>
                                 e.key === "Enter" &&
                                 (e.preventDefault(), addTag())
                              }
                              placeholder="Add tag"
                              className="flex-1 rounded border border-slate-300 px-2 py-1 text-base focus:outline-none focus:ring-1 focus:ring-primary-500"
                           />
                           <button
                              type="button"
                              onClick={addTag}
                              className="rounded bg-slate-100 px-2 py-1 text-xs hover:bg-slate-200"
                           >
                              Add
                           </button>
                        </div>
                        {tagInput && tagSuggestions.length > 0 && (
                           <ul className="absolute z-10 mt-1 w-full rounded border border-slate-200 bg-white shadow-sm max-h-28 overflow-y-auto">
                              {tagSuggestions.map((t) => (
                                 <li key={t}>
                                    <button
                                       type="button"
                                       onClick={() => { update("tags", [...form.tags, t]); setTagInput(""); }}
                                       className="w-full text-left px-2 py-1 text-xs hover:bg-slate-50"
                                    >
                                       {t}
                                    </button>
                                 </li>
                              ))}
                           </ul>
                        )}
                     </div>
                  </div>

                  {/* Checklist */}
                  <ChecklistPanel
                     checklist={checklist}
                     appId={app?.appId}
                     onUpdate={setChecklist}
                  />

                  {/* Disposition — application-only metadata, kept in the lower/detail
                      portion so it does not compete with Company/Role/Status/Priority.
                      Captures why an opportunity stopped progressing; optional. */}
                  {!isNetworking && (
                     <div className="space-y-3 border-t border-slate-100 pt-3">
                        <Select
                           label="Disposition"
                           value={form.disposition}
                           onChange={(v) => update("disposition", v)}
                           options={["", ...DISPOSITION_ORDER]}
                           labels={{ "": "None", ...DISPOSITION_LABELS }}
                        />
                        {form.disposition && (
                           <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">
                                 Disposition note
                              </label>
                              <textarea
                                 value={form.dispositionNote}
                                 onChange={(e) => update("dispositionNote", e.target.value)}
                                 rows={2}
                                 maxLength={500}
                                 placeholder="Add context about why this opportunity stopped progressing"
                                 className="w-full rounded border border-slate-300 px-2 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500 resize-y"
                              />
                           </div>
                        )}
                     </div>
                  )}

                  {/* Debrief (conditional) */}
                  {showDebrief && (
                     <DebriefSection debrief={debrief} onUpdate={setDebrief} />
                  )}

                  {/* Notes (create mode) */}
                  {!isEdit && (
                     <Textarea
                        label="Notes"
                        value={form.notes}
                        onChange={(v) => update("notes", v)}
                        rows={3}
                     />
                  )}
                  {!isEdit && (
                     <Textarea
                        label="Job Description"
                        value={form.jobDescription}
                        onChange={(v) => update("jobDescription", v)}
                        rows={4}
                     />
                  )}
               </form>
            )}

            {tab === "timeline" && isEdit && (
               <ActivityTimeline statusHistory={app.statusHistory} />
            )}

            {tab === "notes" && isEdit && (
               <div className="space-y-3">
                  <Textarea
                     label="Notes"
                     value={form.notes}
                     onChange={(v) => update("notes", v)}
                     rows={30}
                  />
               </div>
            )}

            {tab === "jobDesc" && isEdit && (
               <div className="space-y-3">
                  <Textarea
                     label="Job Description"
                     value={form.jobDescription}
                     onChange={(v) => update("jobDescription", v)}
                     rows={20}
                  />
               </div>
            )}
         </div>

         <div className="flex justify-between border-t border-slate-200 px-5 py-3">
            {isEdit ? (
               <div className="flex gap-2">
                  <button
                     type="button"
                     onClick={() => setShowDelete(true)}
                     className="rounded px-3 py-1.5 text-sm text-destructive-600 hover:bg-destructive-500/10"
                  >
                     Delete
                  </button>
                  {isNetworking && (
                     <button
                        type="button"
                        onClick={handleDuplicate}
                        disabled={submitting}
                        className="rounded px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 font-medium"
                     >
                        Duplicate
                     </button>
                  )}
                  {isNetworking && (
                     <button
                        type="button"
                        onClick={handleConvert}
                        disabled={submitting}
                        className="rounded px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 font-medium"
                     >
                        Convert to Application
                     </button>
                  )}
               </div>
            ) : (
               <div />
            )}
            <div className="flex gap-2">
               <button
                  type="button"
                  onClick={onClose}
                  className="rounded px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
               >
                  Cancel
               </button>
               <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="rounded bg-primary-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
               >
                  {submitting ? "Saving…" : isEdit ? "Update" : "Create"}
               </button>
            </div>
         </div>

         {showDelete && (
            <DeleteConfirmDialog
               app={app}
               onClose={() => {
                  setShowDelete(false);
                  onClose();
               }}
            />
         )}
      </Modal>
   );
}

function getInitialForm(app, isNetworking = false) {
   return {
      company: app?.company || "",
      role: app?.role || (isNetworking ? "TBD" : ""),
      status: app?.status || (isNetworking ? "target" : Status.SAVED),
      dateApplied: app?.dateApplied || "",
      jobUrl: app?.jobUrl || "",
      jobDescription: app?.jobDescription || "",
      salary: app?.salary || {
         min: "",
         max: "",
         currency: "USD",
         period: SalaryPeriod.ANNUAL,
      },
      notes: app?.notes || "",
      location: app?.location || "",
      contactName: app?.contactName || "",
      contactEmail: app?.contactEmail || "",
      source: app?.source || "",
      priority: app?.priority || "",
      disposition: app?.disposition || "",
      dispositionNote: app?.dispositionNote || "",
      nextAction: app?.nextAction || "",
      nextActionDate: app?.nextActionDate || "",
      lastContacted: app?.lastContacted || "",
      tags: app?.tags || [],
   };
}

function Input({ label, value, onChange, type = "text", ...props }) {
   return (
      <div>
         <label className="block text-xs font-medium text-slate-600 mb-1">
            {label}
         </label>
         <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500"
            {...props}
         />
      </div>
   );
}

function Select({ label, value, onChange, options, labels }) {
   return (
      <div>
         <label className="block text-xs font-medium text-slate-600 mb-1">
            {label}
         </label>
         <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500"
         >
            {options.map((opt) => (
               <option key={opt} value={opt}>
                  {labels ? labels[opt] || opt : opt || "—"}
               </option>
            ))}
         </select>
      </div>
   );
}

function Textarea({ label, value, onChange, rows = 3 }) {
   return (
      <div>
         <label className="block text-xs font-medium text-slate-600 mb-1">
            {label}
         </label>
         <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500 resize-y"
         />
      </div>
   );
}
